<script setup lang="ts">
type Tab = { title: string; index: number; disabled?: boolean; active?: boolean }

const { t } = useI18n()

const tabs = reactive([] as Tab[])
const currentTab = ref(0)

const emit = defineEmits<{
  'change-tab': [number]
}>()

const addTab = (tab: Tab) => {
  tab.index = tabs.length

  tabs.push(tab)

  if (tabs.length === 1) {
    currentTab.value = tabs[0].index
  }

  return tab.index
}

const setTab = (tab: Tab) => {
  const index = tabs.findIndex((t) => t.index === tab.index)
  if (index !== -1) {
    tabs[index] = tab
  }
}

const changeTab = (tab: Tab) => {
  if (tab.disabled) {
    return
  }

  currentTab.value = tab.index
  emit('change-tab', tab.index)
}

provide('addTab', addTab)
provide('setTab', setTab)
provide('currentTab', readonly(currentTab))

const next = () => {
  if (currentTab.value < tabs.length - 1) {
    currentTab.value++
    emit('change-tab', currentTab.value)
  }
}

const back = () => {
  if (currentTab.value > 0) {
    currentTab.value--
    emit('change-tab', currentTab.value)
  }
}

defineExpose({ next, back })
</script>
<template>
  <nav aria-label="Progress" class="flex flex-row justify-end gap-3 border-b border-b-border bg-white p-4 px-6">
    <h1 class="grow font-medium">{{ t('pages.import.importPage.title') }}</h1>
    <div v-for="tab in tabs" :key="tab.index" class="flex items-center gap-3">
      <button
        class="flex flex-row items-center gap-3"
        :class="[tab.disabled ? 'pointer-events-none' : '']"
        @click="changeTab(tab)"
      >
        <div
          :class="[
            'flex h-6 w-6 items-center justify-center rounded-full text-xs transition',
            tab.active ? 'bg-primary text-white' : 'bg-primary/5 text-primary'
          ]"
        >
          {{ tab.index + 1 }}
        </div>
        <span
          :class="[
            'text-xs font-medium',
            tab.active && (tab.index === tabs.length - 1 || tabs[tab.index + 1]?.active === false)
              ? 'text-primary'
              : 'text-muted-foreground'
          ]"
        >
          {{ tab.title }}
        </span>
      </button>
      <div
        v-if="tab.index !== tabs.length - 1"
        :class="['w-14 border-y transition', tabs[tab.index + 1]?.active ? 'border-y-primary' : 'border-y-border']"
      ></div>
    </div>
  </nav>
  <div class="flex w-screen grow flex-col items-center overflow-y-auto bg-muted">
    <slot></slot>
  </div>
</template>
